import { useRouter } from 'vue-router';
import { getHostRouter } from '@Src/mfe.config';
import { useRequestFlowStore } from '@Stores/requestFlow.store';

const useFlowNavigation = () => {
    const router = useRouter();
    const hostRouter = getHostRouter();
    const requestFlowStore = useRequestFlowStore();

    const advanceFlow = (): Promise<unknown> | void => {
        const currentQuery = router.currentRoute.value.query;
        const nextTask = requestFlowStore.getNextFlowTask();

        return nextTask ?
            router.push({ name: nextTask.routeName, query: currentQuery }) :
            continueMainFlow();
    };

    const goBackInFlow = () => {
        const currentQuery = router.currentRoute.value.query;
        let prevTask = requestFlowStore.getPreviousFlowTask();

        while (prevTask) {
            const resolved = router.resolve({ name: prevTask.routeName });
            if (!resolved.meta?.bootstrap) break;
            prevTask = requestFlowStore.getPreviousFlowTask();
        }

        prevTask ?
            router.push({ name: prevTask.routeName, query: currentQuery }) :
            navigateToMfeStart();
    };

    const advanceFlowReplace = () => {
        const currentQuery = router.currentRoute.value.query;
        const nextTask = requestFlowStore.getNextFlowTask();

        nextTask ?
            router.replace({ name: nextTask.routeName, query: currentQuery }) :
            continueMainFlow();
    };

    /** Skip to the first task of the next flow type (e.g. when user opts out of current flow). */
    const skipToNextFlowType = () => {
        const currentQuery = router.currentRoute.value.query;
        const nextTask = requestFlowStore.getNextFlowTypeFirstTask();

        nextTask ?
            router.push({ name: nextTask.routeName, query: currentQuery }) :
            continueMainFlow();
    };

    const resumeFlow = () => {
        const currentQuery = router.currentRoute.value.query;
        const nextTask = requestFlowStore.getCurrentFlowTask();

        nextTask ?
            router.replace({ name: nextTask.routeName, query: currentQuery }) :
            continueMainFlow();
    };

    const continueMainFlow = () => {
        const companySlug = hostRouter.currentRoute.value.params.companySlug;
        hostRouter.push({
            name: 'continue-flow',
            params: { companySlug }
        });
    };

    const navigateToMfeStart = () => {
        const companySlug = hostRouter.currentRoute.value.params.companySlug;
        hostRouter.push({ name: 'dashboard', params: { companySlug } });
    };

    const hasRouteInFlow = (routeName: string) => requestFlowStore.hasRouteInFlow(routeName);

    const navigateToRoute = (routeName: string) => {
        if (!hasRouteInFlow(routeName)) {
            console.warn(`navigateToRoute: "${routeName}" is not in the current flow; navigation skipped`);
            return;
        }
        const currentQuery = router.currentRoute.value.query;
        router.push({ name: routeName, query: currentQuery });
    };

    /** After MPP opt-out or purchase: resume the wire leg of the current flow. */
    const resumeWireFlowStep = (): boolean => {
        const currentQuery = router.currentRoute.value.query;

        if (hasRouteInFlow('wire-instructions-ready')) {
            router.replace({ name: 'wire-instructions-ready', query: currentQuery });
            return true;
        }

        if (hasRouteInFlow('wire-instructions')) {
            router.replace({ name: 'wire-instructions', query: currentQuery });
            return true;
        }

        return false;
    };

    return {
        advanceFlow,
        advanceFlowReplace,
        goBackInFlow,
        skipToNextFlowType,
        resumeFlow,
        continueMainFlow,
        hasRouteInFlow,
        navigateToRoute,
        resumeWireFlowStep,
    };
};

export default useFlowNavigation;